import { useReducer } from 'react';
import type { SortOrder as TableSortOrderValue } from '@/components/common/Table/types';
import { SortingOrder as TableSortingOrder } from '@/components/common/Table/types';
import { GroupSortingField as DefaultSortField, SortingOrder as GqlSortingOrder } from '@/gql/enums';
import type { GroupFilteringRule, GroupSortingRule } from '@/gql/graphql';

// Define the shape of the sort state, including the table's display value
type SortRule = GroupSortingRule & { orderValue: TableSortOrderValue | undefined };

// Define the state shape for the group list
export interface State {
    page: number;
    pageSize: number;
    sort: SortRule;
    filter: GroupFilteringRule; // Use the GraphQL filter type
}

// Define Action Types
export interface SetFilterAction {
    type: 'setFilter';
    filter: GroupFilteringRule;
}
export interface SetSortAction {
    type: 'setSort';
    sortBy: SortRule;
}
export interface SetPageAction {
    type: 'setPage';
    page: number;
}
export interface SetPageSizeAction {
    type: 'setPageSize';
    pageSize: number;
}

export type Action = SetFilterAction | SetSortAction | SetPageAction | SetPageSizeAction;

// The reducer function
const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'setFilter':
            // Reset to page 1 when filter changes
            return { ...state, page: 1, filter: action.filter };

        case 'setSort':
            // Reset to page 1 when sort changes
            return { ...state, page: 1, sort: action.sortBy };

        case 'setPage':
            return { ...state, page: action.page };

        case 'setPageSize':
            // Reset to page 1 when page size changes
            return { ...state, page: 1, pageSize: action.pageSize };

        default:
            return state;
    }
};

// Initial state definition
const initialState: State = {
    page: 1,
    pageSize: 10, // Default page size
    sort: {
        field: DefaultSortField.Name, // Default sort field
        order: GqlSortingOrder.Asc, // Default sort order (GraphQL enum)
        orderValue: TableSortingOrder.Asc, // Use the directly imported Enum member
    },
    filter: {}, // Initial filter is empty
};

// The custom hook
const useGroupListReducer = () => useReducer(reducer, initialState);

export default useGroupListReducer;
